import React from "react";
import PropTypes from "prop-types";
import ServiceLinksSection from "./ServiceLinksSection";
import DockerContainersSection from "./DockerContainersSection";
import "./InstanceRow.css";

const DeployProgressBar = ({ progress, status }) => {
  if (progress >= 100) return null;
  
  return (
    <div className="deploy-progress">
      <div className="deploy-progress-bar">
        <div 
          className="deploy-progress-fill" 
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="deploy-progress-text">
        {progress}% - {status}
      </span>
    </div>
  );
};

const InstanceRow = ({
  instance,
  expandedInstance,
  toggleInstanceExpand,
  getStateDisplayText,
  getTimeRemaining,
  handleShowLogs,
  handleShutdown,
  handleSetIndefinite,
  handleUploadConfig,
  settingIndefinite,
  destroyingStack,
  isServiceHealthy,
  loadingDocker,
  dockerContainers,
  handleShowContainerLogs,
  handleRestartContainer,
  isAdmin = false,
  deployProgress,
  users = [],
  onChangeOwner = null,
}) => {
  const onContainerUpload = (containerName) => {
    handleUploadConfig(instance, containerName);
  };
  const isExpanded = expandedInstance?.instanceId === instance.instanceId;
  const isRunning = instance.state === "running";
  const hasPublicIp = instance.publicIp !== "N/A";
  const isDeployingState = instance.state === "deploying";
  const isFailedState = instance.state === "failed";
  const isDeploying = isDeployingState || (deployProgress && !deployProgress.isComplete);
  const isLogsDisabled = !isRunning || !hasPublicIp || isDeployingState || isFailedState;
  const isShutdownDisabled =
    destroyingStack === instance.pulumiStack ||
    instance.state === "stopping" ||
    instance.state === "stopped" ||
    isDeployingState;

  const getShutdownTitle = () => {
    if (isDeployingState) return "Instance is still deploying";
    if (instance.state === "stopping") return "Instance is already stopping";
    if (instance.state === "stopped") return "Instance is already stopped";
    return "";
  };

  const getLogsTitle = () => {
    if (isDeployingState) return "Instance is still deploying";
    if (isFailedState) return "Deployment failed";
    if (isRunning && hasPublicIp) return "";
    return "Instance must be running to view logs";
  };

  const getUserDisplayName = (user) => {
    if (!user) return "-";
    const { firstName, lastName, email } = user;
    if (firstName || lastName) {
      return `${firstName || ""} ${lastName || ""}`.trim();
    }
    return email;
  };

  const colSpan = isAdmin ? 7 : 6;

  const canExpand = isRunning && !isDeploying;

  return (
    <React.Fragment key={instance.instanceId}>
      <tr
        className={`instance-row ${isExpanded ? "expanded" : ""}`}
        onClick={() => canExpand && toggleInstanceExpand(instance)}
        style={{ cursor: canExpand ? "pointer" : "default" }}
      >
        <td className="instance-id">
          <span className="expand-icon">{canExpand ? (isExpanded ? "▼" : "▶") : ""}</span>
          {instance.instanceId}
        </td>
        <td>{instance.tags?.Name || "-"}</td>
        {isAdmin && (
          <td className="owner-cell" onClick={(e) => e.stopPropagation()}>
            {users?.length > 0 && onChangeOwner ? (
              <select
                className="owner-select"
                value={instance.owner?.id || ""}
                onChange={(e) => onChangeOwner(instance.instanceId, e.target.value)}
              >
                {!instance.owner && <option value="">Unassigned</option>}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {getUserDisplayName(u)}
                  </option>
                ))}
              </select>
            ) : (
              <span className="owner-badge">{getUserDisplayName(instance.owner)}</span>
            )}
          </td>
        )}
        <td>
          <div className="state-cell">
            {isDeploying && deployProgress ? (
              <DeployProgressBar 
                progress={deployProgress.progress} 
                status={deployProgress.status} 
              />
            ) : (
              <span className={`state-badge state-${instance.state}`}>
                {getStateDisplayText(instance.state)}
              </span>
            )}
          </div>
        </td>
        <td>{instance.publicIp}</td>
        <td>
          <div className="auto-destroy-cell">
            {instance.indefinite ? (
              <span className="indefinite-badge">∞</span>
            ) : instance.destroyAt ? (
              <span
                className={`countdown ${
                  getTimeRemaining(instance.destroyAt)?.includes("Expired") ? "expired" : ""
                }`}
              >
                {getTimeRemaining(instance.destroyAt)}
              </span>
            ) : (
              <span className="no-action">-</span>
            )}
            {isAdmin && (
              <button
                className={`indefinite-btn ${instance.indefinite ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetIndefinite(instance.instanceId, !instance.indefinite);
                }}
                disabled={settingIndefinite === instance.instanceId}
                title={instance.indefinite ? "Re-enable auto-destroy" : "Make indefinite"}
              >
                {settingIndefinite === instance.instanceId ? "..." : instance.indefinite ? "⏱" : "∞"}
              </button>
            )}
          </div>
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="action-buttons">
            <button
              className="logs-btn"
              onClick={() => handleShowLogs(instance)}
              disabled={isLogsDisabled}
              title={getLogsTitle()}
            >
              Logs
            </button>
            {instance.pulumiStack ? (
              <button
                className="shutdown-btn"
                onClick={() => handleShutdown(instance)}
                disabled={isShutdownDisabled}
                title={getShutdownTitle()}
              >
                {destroyingStack === instance.pulumiStack ? "Destroying..." : "Shutdown"}
              </button>
            ) : (
              <span className="no-action">-</span>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="expanded-row">
          <td colSpan={colSpan}>
            <div className="docker-info">
              {hasPublicIp && (
                <ServiceLinksSection
                  publicIp={instance.publicIp}
                  isServiceHealthy={isServiceHealthy}
                  dockerContainers={dockerContainers}
                />
              )}
              <DockerContainersSection
                loadingDocker={loadingDocker}
                dockerContainers={dockerContainers}
                handleShowContainerLogs={handleShowContainerLogs}
                handleRestartContainer={handleRestartContainer}
                handleUploadConfig={onContainerUpload}
              />
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const InstanceCard = ({
  instance,
  expandedInstance,
  toggleInstanceExpand,
  getStateDisplayText,
  getTimeRemaining,
  handleShowLogs,
  handleShutdown,
  handleSetIndefinite,
  handleUploadConfig,
  settingIndefinite,
  destroyingStack,
  isServiceHealthy,
  loadingDocker,
  dockerContainers,
  handleShowContainerLogs,
  handleRestartContainer,
  isAdmin = false,
  deployProgress,
  users = [],
  onChangeOwner = null,
}) => {
  const onContainerUpload = (containerName) => {
    handleUploadConfig(instance, containerName);
  };
  const isExpanded = expandedInstance?.instanceId === instance.instanceId;
  const isRunning = instance.state === "running";
  const hasPublicIp = instance.publicIp !== "N/A";
  const isDeployingState = instance.state === "deploying";
  const isFailedState = instance.state === "failed";
  const isDeploying = isDeployingState || (deployProgress && !deployProgress.isComplete);
  const isLogsDisabled = !isRunning || !hasPublicIp || isDeployingState || isFailedState;
  const isShutdownDisabled =
    destroyingStack === instance.pulumiStack ||
    instance.state === "stopping" ||
    instance.state === "stopped" ||
    isDeployingState;

  const getUserDisplayName = (user) => {
    if (!user) return null;
    const { firstName, lastName, email } = user;
    if (firstName || lastName) {
      return `${firstName || ""} ${lastName || ""}`.trim();
    }
    return email;
  };

  const canExpand = isRunning && !isDeploying;

  return (
    <div className={`instance-card-mobile ${isExpanded ? "expanded" : ""}`}>
      <div
        className="instance-card-header"
        onClick={() => canExpand && toggleInstanceExpand(instance)}
        style={{ cursor: canExpand ? "pointer" : "default" }}
      >
        <div className="instance-card-title">
          <span className="expand-icon">{canExpand ? (isExpanded ? "▼" : "▶") : ""}</span>
          <span className="instance-name">{instance.tags?.Name || "Unnamed"}</span>
          {(!isDeploying || !deployProgress) && (
            <span className={`state-badge state-${instance.state}`}>
              {getStateDisplayText(instance.state)}
            </span>
          )}
        </div>
        {isAdmin && (
          <div className="instance-card-owner" onClick={(e) => e.stopPropagation()}>
            {users?.length > 0 && onChangeOwner ? (
              <select
                className="owner-select"
                value={instance.owner?.id || ""}
                onChange={(e) => onChangeOwner(instance.instanceId, e.target.value)}
              >
                {!instance.owner && <option value="">Unassigned</option>}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {getUserDisplayName(u)}
                  </option>
                ))}
              </select>
            ) : (
              getUserDisplayName(instance.owner) && (
                <span className="owner-badge">{getUserDisplayName(instance.owner)}</span>
              )
            )}
          </div>
        )}
        {isDeploying && deployProgress && (
          <DeployProgressBar 
            progress={deployProgress.progress} 
            status={deployProgress.status} 
          />
        )}
      </div>

      <div className="instance-card-details">
        <div className="instance-card-row">
          <span className="label">IP</span>
          <span className="value">{instance.publicIp}</span>
        </div>
        <div className="instance-card-row">
          <span className="label">Auto-Destroy</span>
          <div className="auto-destroy-cell">
            {instance.indefinite ? (
              <span className="indefinite-badge">∞ Indefinite</span>
            ) : instance.destroyAt ? (
              <span
                className={`countdown ${
                  getTimeRemaining(instance.destroyAt)?.includes("Expired") ? "expired" : ""
                }`}
              >
                {getTimeRemaining(instance.destroyAt)}
              </span>
            ) : (
              <span className="no-action">-</span>
            )}
            {isAdmin && (
              <button
                className={`indefinite-btn ${instance.indefinite ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetIndefinite(instance.instanceId, !instance.indefinite);
                }}
                disabled={settingIndefinite === instance.instanceId}
                title={instance.indefinite ? "Re-enable auto-destroy" : "Make indefinite"}
              >
                {settingIndefinite === instance.instanceId ? "..." : instance.indefinite ? "⏱" : "∞"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="instance-card-actions">
        <button
          className="logs-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleShowLogs(instance);
          }}
          disabled={isLogsDisabled}
        >
          Logs
        </button>
        {instance.pulumiStack && (
          <button
            className="shutdown-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleShutdown(instance);
            }}
            disabled={isShutdownDisabled}
          >
            {destroyingStack === instance.pulumiStack ? "Destroying..." : "Shutdown"}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="instance-card-expanded">
          {hasPublicIp && (
            <ServiceLinksSection
              publicIp={instance.publicIp}
              isServiceHealthy={isServiceHealthy}
              dockerContainers={dockerContainers}
            />
          )}
          <DockerContainersSection
            loadingDocker={loadingDocker}
            dockerContainers={dockerContainers}
            handleShowContainerLogs={handleShowContainerLogs}
            handleRestartContainer={handleRestartContainer}
            handleUploadConfig={onContainerUpload}
          />
        </div>
      )}
    </div>
  );
};

InstanceCard.propTypes = {
  instance: PropTypes.object.isRequired,
  expandedInstance: PropTypes.object,
  toggleInstanceExpand: PropTypes.func.isRequired,
  getStateDisplayText: PropTypes.func.isRequired,
  getTimeRemaining: PropTypes.func.isRequired,
  handleShowLogs: PropTypes.func.isRequired,
  handleShutdown: PropTypes.func.isRequired,
  handleSetIndefinite: PropTypes.func.isRequired,
  handleUploadConfig: PropTypes.func.isRequired,
  settingIndefinite: PropTypes.string,
  destroyingStack: PropTypes.string,
  isServiceHealthy: PropTypes.func.isRequired,
  handleRestartContainer: PropTypes.func.isRequired,
  loadingDocker: PropTypes.bool.isRequired,
  dockerContainers: PropTypes.array.isRequired,
  handleShowContainerLogs: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  deployProgress: PropTypes.object,
  users: PropTypes.array,
  onChangeOwner: PropTypes.func,
};

export { InstanceCard };

InstanceRow.propTypes = {
  instance: PropTypes.object.isRequired,
  expandedInstance: PropTypes.object,
  toggleInstanceExpand: PropTypes.func.isRequired,
  getStateDisplayText: PropTypes.func.isRequired,
  getTimeRemaining: PropTypes.func.isRequired,
  handleShowLogs: PropTypes.func.isRequired,
  handleShutdown: PropTypes.func.isRequired,
  handleSetIndefinite: PropTypes.func.isRequired,
  handleUploadConfig: PropTypes.func.isRequired,
  settingIndefinite: PropTypes.string,
  destroyingStack: PropTypes.string,
  isServiceHealthy: PropTypes.func.isRequired,
  handleRestartContainer: PropTypes.func.isRequired,
  loadingDocker: PropTypes.bool.isRequired,
  dockerContainers: PropTypes.array.isRequired,
  handleShowContainerLogs: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  deployProgress: PropTypes.object,
  users: PropTypes.array,
  onChangeOwner: PropTypes.func,
};

export default InstanceRow;
